import React, { useState, useRef, forwardRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import styles from './CommentForm.module.scss';
import ReturnIcon from '../Icons/ReturnIcon';
import InlineOpenGraph from '../InlineOpenGraph';
import { useOpenGraph } from '../../hooks/useOpenGraph';
import DescriptionEditor from '../DescriptionEditor/DescriptionEditor';
import logger from '../../utils/logger';

const CommentForm = forwardRef(
  ({ currentUser, addComment, songId, placeholder = 'Add a comment' }, ref) => {
    const [commentHTML, setCommentHTML] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const reducedMotion = useReducedMotion();
    const editorRef = useRef(null);
    const submittingRef = useRef(false);
    const submitEnterDelay = reducedMotion ? 0 : 0.1;
    const submitTransition = reducedMotion
      ? { duration: 0 }
      : { duration: 0.2, ease: [0.25, 0.1, 0.25, 1] };
    const [removedUrls, setRemovedUrls] = useState(new Set());

    const { openGraphData, isLoading: openGraphLoading, error: openGraphError } =
      useOpenGraph(commentHTML);

    const filteredOpenGraphData = openGraphData.filter((d) => !removedUrls.has(d.url));

    const generateUniqueId = () => '_' + Math.random().toString(36).substr(2, 9);

    const handleRemoveOpenGraphCard = (url) => {
      setRemovedUrls((prev) => new Set([...prev, url]));
    };

    const isEmpty = () =>
      !commentHTML || commentHTML.replace(/<[^>]*>/g, '').trim() === '';

    const handleAddComment = () => {
      if (!currentUser || isEmpty() || submittingRef.current) return;
      submittingRef.current = true;

      const text = editorRef.current?.getHTML() ?? '';
      const mentionedHandles = editorRef.current?.getMentionedHandles() ?? [];

      const newComment = {
        userId: currentUser.uid,
        displayName: currentUser.displayName,
        text,
        commentId: generateUniqueId(),
        mentionedHandles,
      };

      // Clear the editor immediately — the comment list updates optimistically.
      editorRef.current?.clear();
      setCommentHTML('');
      setIsFocused(false);
      setRemovedUrls(new Set());

      Promise.resolve(addComment(songId, newComment))
        .catch((error) => {
          logger.error('Error adding comment:', error);
        })
        .finally(() => {
          submittingRef.current = false;
        });
    };

    // Enter (without Shift) submits; returning true tells TipTap the event is consumed.
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleAddComment();
        return true;
      }
      return false;
    };

    return (
      <div className={styles.container}>
        {currentUser ? (
          <img className={styles.avatar} src={currentUser.avatar} alt={currentUser.displayName} />
        ) : (
          <img className={styles.avatar} src="/path/to/default/avatar.png" alt="Guest User" />
        )}
        <div className={styles.editorWrapper}>
          <div className={styles.editorBox}>
            <DescriptionEditor
              ref={editorRef}
              variant="comment"
              formatChromeVisibility="focus"
              placeholder={placeholder}
              maxLength={1000}
              onChange={(html) => setCommentHTML(html)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onKeyDown={handleKeyDown}
            />
            <InlineOpenGraph
              openGraphData={filteredOpenGraphData}
              isLoading={openGraphLoading}
              error={openGraphError}
              onRemoveCard={handleRemoveOpenGraphCard}
              className={styles.inlineOpenGraph}
            />
          </div>
        </div>
        <AnimatePresence mode="popLayout">
          {(isFocused || !isEmpty()) && (
            <motion.div
              key="comment-submit-wrap"
              className={styles.buttonContainer}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{
                opacity: 0,
                x: 8,
                transition: submitTransition,
              }}
              transition={{
                ...submitTransition,
                delay: submitEnterDelay,
              }}
            >
              <button
                className={styles.postButton}
                onClick={handleAddComment}
                disabled={isEmpty() || !currentUser}
              >
                <ReturnIcon /> Submit
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }
);

export default CommentForm;
